// Typed client for the dashboard's read API (SmartWaterCDK's ReadApiResources
// -- see specs/open/smartwater-realtime-pipeline-dashboard.md's Approach item
// 7). Every call is Cognito-authorized via the same ID token auth.ts already
// manages -- the read API's CognitoUserPoolsAuthorizer expects the raw JWT in
// the `Authorization` header (no `Bearer ` prefix -- API Gateway's Cognito
// authorizer validates the token itself, unlike a generic OAuth resource
// server).
import { authService } from '../auth';
import { getDashboardConfig } from '../common/constants';
import {
  ActiveRunResponse,
  LeaguesResponse,
  PastRunsResponse,
  RunEventsResponse,
  RunTimelineResponse,
} from './interfaces';

/** Thrown when a read API call returns a non-2xx status. Carries the status for callers that branch on 404 vs 5xx. */
export class ReadApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ReadApiError';
  }
}

async function readApiFetch<T>(path: string): Promise<T> {
  const { readApiUrl } = getDashboardConfig();
  const idToken = authService.getIdToken();
  if (!idToken) {
    throw new ReadApiError(401, 'No ID token available -- user is not signed in.');
  }

  const response = await fetch(`${readApiUrl}${path}`, {
    headers: { Authorization: idToken },
  });

  if (!response.ok) {
    throw new ReadApiError(response.status, `Read API request to ${path} failed with status ${response.status}`);
  }
  return response.json();
}

/** Lists the leagues that currently have a registered SageMaker pipeline. */
export function listLeagues(): Promise<LeaguesResponse> {
  return readApiFetch<LeaguesResponse>('/leagues');
}

/** Lists past runs for a league, from the run-index table. */
export function listPastRuns(league: string): Promise<PastRunsResponse> {
  return readApiFetch<PastRunsResponse>(`/runs?league=${encodeURIComponent(league)}`);
}

/**
 * Looks up one run's archive entry. Throws a `ReadApiError` with status 404
 * if the run doesn't exist -- callers should treat 404 as "not found," not
 * as an unexpected failure. Returns only the run's latest-state summary --
 * full per-event history is a separate call, `getRunEvents`, per
 * specs/open/smartwater-granular-event-history.md's Approach item 4
 * decision to keep the two routes separate (this one stays fast; the
 * events route is called on demand only when a user drills in).
 */
export function getRunTimeline(runId: string): Promise<RunTimelineResponse> {
  return readApiFetch<RunTimelineResponse>(`/runs/${encodeURIComponent(runId)}`);
}

/**
 * Fetches one page of a run's full event history, optionally scoped to a
 * single step (`stepRole`) and/or filtered by minimum step_log severity
 * (`minLevel`, defaults to the backend's own default of 'info' when
 * omitted). Pass a previous response's `nextToken` to fetch the next page
 * -- see specs/open/smartwater-granular-event-history.md's Approach item 4.
 */
export function getRunEvents(
  runId: string,
  options: { stepRole?: string; minLevel?: 'debug' | 'info'; nextToken?: string } = {},
): Promise<RunEventsResponse> {
  const params = new URLSearchParams();
  if (options.stepRole) params.set('stepRole', options.stepRole);
  if (options.minLevel) params.set('minLevel', options.minLevel);
  if (options.nextToken) params.set('nextToken', options.nextToken);
  const query = params.toString();
  return readApiFetch<RunEventsResponse>(
    `/runs/${encodeURIComponent(runId)}/events${query ? `?${query}` : ''}`,
  );
}

/**
 * Returns the league's currently in-progress run, or `null` if there isn't
 * one -- a 404 here is an expected steady state (most page loads happen
 * between runs), not an error, so unlike getRunTimeline this resolves to
 * `null` instead of throwing.
 */
export async function getActiveRun(league: string): Promise<ActiveRunResponse['run'] | null> {
  try {
    const response = await readApiFetch<ActiveRunResponse>(
      `/runs?league=${encodeURIComponent(league)}&active=true`,
    );
    return response.run;
  } catch (error) {
    if (error instanceof ReadApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}
